import _ from 'lodash'
import axios from 'axios'
import 'firebase/analytics'
import 'firebase/auth'
import 'firebase/firestore'
import firebase from 'firebase/app'
import SocketIO from 'socket.io-client'

import {
  ControlTypes,
  LocalStore,
  LocalStoreKeys,
  ReqOps,
  ReqState
} from '@/utils/vars'

let io = null

axios.defaults.baseURL =
  process.env.VUE_APP_CANDELA_SERVER_BASE_URL

/**
 * This is a generic request function used throughout the actions.
 * The intention is for request state variables to be set somewhat
 * automagically when a request is made via this method.
 * That is, that at each stage of the request, a state value is
 * assigned to the request type in the vuex store.
 *
 * Params
 *
 * method - An axios request method such as, axios.get or axios.put
 * url - The url to send the request to
 * data - The body of the request (only applicable for PUT, POST & PATCH)
 * op - The name of the RequestOp (see utils.vars.RequestOp)
 * dispatch - The vuex dispatch function
 * commit - The vuex commit function (defaults to no op)
 *
 * Example:
 * request(
 *  axios.put, '/users/242034823908/',
 *    {username: Sonic}, ReqOps.UpdateUser, dispatch, commit
 *  )
 *
 * When the request method is first called the state of the request op will
 * be set in the store to ReqState.Request, indicating that the request has
 * been sent. When it completes, the state is set to ReqState.Complete and in
 * the case of an error the state is set to ReqState.Error
 *
 * This is convenient because you can check the state of a request type from
 * anywhere in the program. This method may not be suitable for all use cases.
 * If not, you can also use the typical .then()/.catch() approach where needed.
 */
const request = async (method, url, data, op, dispatch, commit = () => {}) => {
  const id_token = await firebase.auth().currentUser.getIdToken(true)
  const headers = {
    AuthToken: id_token,
    'Content-Type': 'application/json'
  }

  const create_req = (method, data, headers) => {
    const is_write =
      method === axios.post ||
      method === axios.put ||
      method === axios.patch
    return is_write ? method(url, data, {headers}) : method(url, {headers})
  }

  return new Promise((resolve, reject) => {
    commit('set_req_state', [op, ReqState.Request])
    create_req(method, data, headers)
      .then(res => {
        resolve(res)
        commit('set_req_state', [op, ReqState.Complete])
      }).catch(err => {
        switch(err.response.status) {
          case 403: // Unauthorized -
            // Sign this person out and return to login
            // TODO: Redirect to an error screen for better UX
            dispatch('logout')
            reject(err)
            break
          default:
            // Some other error occurred
            commit('set_req_error', [
              ReqOps.CreateApp,
              err && err.response ? err.response.data : err
            ])
            commit('set_req_state', [op, ReqState.Error])
            reject(err)
        }
      })
  })
}

export default {
  /**
   * Clears log messages
   */
  clear_log({commit}) {
    commit('clear_log')
  },
  /**
   * Clears the current state of the supplied req_op
   */
  clear_req_op({commit}, req_op) {
    commit('set_req_state', [req_op, ReqState.None])
  },
  /**
   * Connects to the SocketIO server and sets up handlers
   */
  async connect({commit, dispatch, getters}, app_id) {
    // Set the request state for ReqOp.Connect
    commit('set_req_state', [ReqOps.Connect, ReqState.Request])

    // Clear an existing connection and app
    if(io) io.close()
    commit('clear_app')

    // If this is not a new app, it has a saved layout
    // Fetch the layout (and settings once that feature is in place)
    // before loading the app.
    await request(
      axios.get, `/app-layout/${app_id}`, null,
      ReqOps.AppLayout, dispatch, commit)
      .then(req => {
        commit('set_saved_app_layout', req.data)
      })

    await request(
      axios.get, `/app-settings/${app_id}`, null,
      ReqOps.AppSettings, dispatch, commit)
      .then(res => {
        commit('set_saved_app_settings', res.data)
      })

    await request(
      axios.get, `/app-controls/${app_id}`, null,
      ReqOps.AppSettings, dispatch, commit)
      .then(res => {
        commit('set_saved_app_controls', res.data)
      })

    // Connect to the Candela server
    const user_id = getters.user.id
    const id_token = await firebase.auth().currentUser.getIdToken(true)
    const headers = {
      AuthToken: id_token,
      'Content-Type': 'application/json'
    }

    const base_url = process.env.VUE_APP_CANDELA_SERVER_BASE_URL
    const addr = `${base_url}/${app_id}`

    io = SocketIO(addr, {
      extraHeaders: headers,
      forceNew: true,
      query: {
        client: 'candela-web-frontend',
        user: user_id
      }
    })

    // Called when this client has connected to the server
    io.on('connect', () => {
      commit('set_connection', {app_id, connected: true})
      commit('set_req_state', [ReqOps.Connect, ReqState.Complete])
    })

    // Called when there has been an error connecting to the server
    io.on('connect_error', () => {
      commit('set_connection', {app_id, connected: false})
      commit('set_req_state', [ReqOps.Connect, ReqState.Error])
    })

    // Called when this client disconnects from the server
    io.on('disconnect', () => {
      commit('set_connection', {app_id, connected: false})
    })

    // Called when an unspecified error has occurred
    io.on('error', e => {
      commit('set_connection', {app_id, connected: false})
    })

    // Called when the Candela client app logs to this user
    io.on('log', data => {
      dispatch('log', data)
    })

    // Called when there has been an re-connection error
    io.on('reconnect_error', e => {
      commit('set_connection', {app_id, connected: false})
    })

    // Called each time the Candela client has new data for this user
    io.on('update_components', data => {
      dispatch('update_components', Object.values(data))
    })
  },
  /**
   * Create a new Candela app
   */
  async create_app({commit, dispatch, getters}, app_data) {
    const data = {...app_data, user: getters.user.id}
    return new Promise((resolve, reject) => {
      request(axios.post, '/app', data, ReqOps.CreateApp, dispatch, commit)
        .then(res => {
          commit('add_app', res.data)
          resolve(res.data)
        })
    })
  },
  /**
   * Completely delete a user's account, including all associated data
   */
  async delete_account({commit, dispatch, getters}) {
    return request(
      axios.delete, `/user/${getters.user.id}`, null, ReqOps.DeleteAccount, dispatch, commit)
      .then(() => {
        if(io) io.close()
        dispatch('logout')
      })
  },
  /**
   * Delete a single Candela app
   */
  async delete_app({commit, dispatch}, app_id) {
    return request(
      axios.delete, `/app/${app_id}`, null, ReqOps.DeleteApp, dispatch, commit)
      .then(res => {
        commit('delete_app', app_id)
      })
  },
  /**
   * Add a log messages
   */
  log({commit}, data) {
    commit('update_log', data)
  },
  /**
   * Set highlighted state of a single component
   */
  set_component_highlight({commit}, {id, highlight}) {
    commit('set_component_highlight', {id, highlight})
  },
  /**
   * Initialize applications
   * Setups our Firebase authentication and logs the user into
   * the Candela server
   */
  async init_app({commit, dispatch}) {
    const is_dark = LocalStore.getItem(LocalStoreKeys.DarkTheme)
    commit('set_dark_theme', is_dark === 'true')
  },
  /**
   * Load the user's API key
   */
  async load_api_key({commit, dispatch, getters}) {
    return request(
      axios.get, `/api-key/${getters.user.id}`, null, ReqOps.APIKey, dispatch, commit)
      .then(res => {
        commit('set_api_key', res.data.api_key)
      })
  },
  /**
   * Load a user's apps
   */
  async load_apps({commit, dispatch, getters}) {
    return request(
      axios.get, `/apps/${getters.user.id}`, null, ReqOps.Apps, dispatch, commit
    ).then(res => {
      commit('set_apps', res.data)
    })
  },
  /**
   * Log in a user
   */
  login({commit, dispatch}, user) {
    return request(axios.post, '/login', user, ReqOps.Login, dispatch, commit)
      .then(res => {
        commit('set_user', res.data)
      })
  },
  /**
   * Log out a currently logged in user
   */
  logout({commit}) {
    // Clear an existing connection and app
    if(io) io.close()
    commit('clear_app')
    commit('clear_user')
    commit('reset_state')
    axios.post('/logout')
  },
  /**
   * Save an existing Candela app
   */
  async save_app({commit, dispatch}, app_data) {
    return request(axios.put, `/app/${app_data.id}`,
      app_data, ReqOps.SaveApp, dispatch, commit)
      .then(res => {
        commit('update_app', res.data)
      })
  },
  /**
   * Save the layout of the current app
   */
  save_app_layout: _.throttle(({commit, getters}, layout) => {
    const app_id = getters.connection.app_id
    if(app_id) {
      const layout = Object.entries(getters.components).reduce((acc, [k, v]) => {
        acc[k] = v.layout
        return acc
      }, {})

      request(axios.put, `/app-layout/${app_id}`, layout)
        .then(res => {
          commit('set_saved_app_layout', res.data)
        })
    }
  }, 1000, {trailing: true}),
  /**
   * Sends a control event from the user interface to the Candela server
   */
  send_control_value: _.throttle(({commit}, {id, component_id, type, value}) => {
    if(!io) return
    io.emit('control_event', {
      id,
      component_id: component_id || id,
      type,
      value
    })
  }, 100, {trailing: true}),
  /**
   * Several components are interested in window resize events
   * This serves to set a globally available variable representing
   * the application width and height
   *
   * TODO: We may want to keep a couple of different "frames", like a resize_frame
   * and a update_frame. Or considering invoking resize handlers in a different
   * way all together.
   */
  set_app_size({commit, dispatch, getters}, new_size) {
    commit('set_app_size', new_size)
    Object.keys(getters.components).forEach(v => {
      dispatch('update_component_frame', v)
    })
  },
  /**
   * Update a single setting for a given component
   * Expected format: {
   *    component_id: 'some-component',
   *    setting_id:'line-thickness', value: 4
   *  }
   */
  set_component_setting({commit, state}, data) {
    request(axios.put,
      `app-settings/${state.connection.app_id}`, {
        app: state.connection.app_id,
        [data.component_id]: {
          [data.setting_id]: data.value
        }
      })
    commit('set_component_setting', data)
  },
  /**
   * Sets the light/dark theme for the app
   */
  set_dark_theme({commit}, is_dark) {
    commit('set_dark_theme', is_dark)
  },
  /**
   * Can be used to force an updated for a given component
   */
  update_component_frame({commit}, id) {
    commit('update_component_frame', id)
  },
  /**
   * Sets the layout data for a give component
   */
  update_component_layout({commit}, layout) {
    layout.forEach(l => {
      commit('update_component_layout', {...l})
    })
  },
  /**
   * Update all of the components with the provided data
   */
  update_components({commit, dispatch, state}, components) {
    components
      .filter(c => !ControlTypes.includes(c.type))
      .forEach(c => commit('update_component', c))
  }
}
